import React, { useEffect, useState } from 'react';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';

import { TextInputArea } from '@uhg-abyss/web/ui/TextInputArea';
import { FileUpload } from '@uhg-abyss/web/ui/FileUpload';
import { styled } from '@uhg-abyss/web/tools/styled';
import { DateInput } from '@uhg-abyss/web/ui/DateInput';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { RadioGroup } from '@uhg-abyss/web/ui/RadioGroup';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import PropTypes from 'prop-types';
import { LagoonContext } from '@src/AppContext';
import { useDownloadFile } from '@src/hooks/useDownloadFile/useDownloadFile';
import { V2SelectInput } from '@uhg-abyss/web/ui/SelectInput';
const Wrapper = styled('div', {
  '.abyss-descriptors-error-message': {
    fontSize: '13px ',
    fontWeight: '400',
  },
});

export const CreateCommunicationFormFields = ({ setDraftFiles }) => {
  const form = useForm();
  const isFilesAvailable = form.getValues('isFilesAvailable');
  const [downloadFile] = useDownloadFile();
  const lagoonContext = React.useContext(LagoonContext);
  const commCornerAudience = lagoonContext?.getTable(
    'communication-corner-audience'
  );

  // State for API list options
  const [apiOptions, setApiOptions] = useState([]);
  const [selectedAudience, setSelectedAudience] = useState([]);

  const symbol = () => {
    return <span style={{ color: '#C40000', fontSize: '23px' }}>*</span>;
  };

  const currentDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    return date;
  };

  const nextTwoMonths = () => {
    const date = new Date();
    date.setMonth(date.getMonth() + 2);
    return date;
  };

  const fileTypesAccepted = {
    'application/pdf': ['.pdf'],
  };

  const headerContent = (
    <React.Fragment>
      <FileUpload.Heading
        css={{
          'abyss-file-upload-header-heading-text': {
            fontSize: '15px !important',
          },
        }}
      >
        Accepted File Type
      </FileUpload.Heading>
      <FileUpload.Description
        css={{
          'abyss-file-upload-header-description-text': {
            fontSize: '15px !important',
          },
        }}
      >
        PDF (Portable Document Format)
      </FileUpload.Description>
      <FileUpload.Heading
        css={{
          'abyss-file-upload-header-heading-text': {
            marginTop: '15px',
            fontSize: '15px !important',
          },
        }}
      >
        <b>Please do not include any PHI/PII information on the attachments</b>
      </FileUpload.Heading>
    </React.Fragment>
  );

  // Initialize API options when component mounts
  useEffect(() => {
    const initializeApiOptions = () => {
      const apis = [];
      const allRows = commCornerAudience?.getRow();
      allRows?.forEach((row) => {
        if (row?.active) {
          apis.push({
            label: row?.label,
            value: row?.value,
            isDisabled: false,
          });
        }
      });
      setApiOptions(apis);
    };

    initializeApiOptions();
  }, [apiOptions.length]);

  useEffect(() => {
    form.setValue('audience', selectedAudience);
  }, [selectedAudience]);

  // Handle audience selection change
  const handleAudienceChange = (newValue, selectedValues) => {
    // Check if "All Users" is selected
    const hasAllUsers = newValue?.some((option) => {
      return (
        option === 'All Users' || option.toLowerCase().includes('all users')
      );
    });

    if (hasAllUsers) {
      // Find "All Users" option and keep only that selection
      const allUsersOption = newValue.find((option) => {
        return (
          option === 'All Users' || option?.toLowerCase().includes('all users')
        );
      });

      setSelectedAudience([allUsersOption]);

      // Disable all other options except "All Users"
      setApiOptions((prevOptions) => {
        return prevOptions.map((option) => {
          return {
            ...option,
            isDisabled: option.label !== 'All Users',
          };
        });
      });
    } else {
      // No "All Users" selected - enable all options and update selection
      setSelectedAudience(newValue);

      setApiOptions((prevOptions) => {
        return prevOptions.map((option) => {
          return {
            ...option,
            isDisabled: false,
          };
        });
      });
    }
  };

  const validateFile = (file) => {
    const specCharRegex = /[!$%^&*()_+|~=`{}[\]:/;<>?,@#]/;
    if (file.name && specCharRegex.test(file.name)) {
      return {
        message:
          'File name cannot contain special characters other than hyphens.',
      };
    }
    return null;
  };

  return (
    <Wrapper>
      <Layout.Stack
        space={40}
        css={{
          'abyss-layout-stack': {
            display: 'block',
            marginTop: '-2px',
            marginBottom: '20px',
          },
        }}
      >
        <div style={{ fontSize: '15px' }}>Required {symbol()}</div>

        <V2SelectInput
          id="audience"
          label="Audience"
          model="audience"
          width="330px"
          placeholder=""
          isClearable
          isSearchable
          isMulti
          options={apiOptions}
          onChange={handleAudienceChange}
          value={selectedAudience}
          validators={{ required: true }}
          css={{
            'abyss-select-input-label': {
              position: 'relative',
              fontSize: '15px',
            },
          }}
        />
        <Flex justify="space-between">
          <div>
            <DateInput
              id="date"
              label="Release Date"
              model="date"
              width="280px"
              minimumDate={currentDate()}
              maximumDate={nextTwoMonths()}
              validators={{ required: true }}
              subText={
                <span
                  style={{
                    fontWeight: '1',
                    color: '#7d7f81',
                    fontSize: '13px',
                  }}
                >
                  Date Format: mm/dd/yyyy
                </span>
              }
              css={{
                'abyss-date-picker-input-label': {
                  position: 'relative',
                  fontSize: '15px',
                },
              }}
            />
          </div>
          <div>
            <TextInput
              id="subject"
              label="Subject/RE:"
              type="text"
              model="subject"
              width="330px"
              isClearable
              validators={{ required: true }}
              css={{
                'abyss-text-input-label': {
                  position: 'relative',
                  fontSize: '15px',
                },
              }}
            />
          </div>
        </Flex>
        <TextInputArea
          id="topic"
          label="Topic"
          model="topic"
          displayMaxLength
          validators={{ required: true }}
          css={{
            'abyss-text-input-area-label': {
              position: 'relative',
              fontSize: '15px',
            },
          }}
        />
        <TextInputArea
          id="actionByUnitedHealthCare"
          label="Action by UnitedHealthcare"
          model="actionByUnitedHealthCare"
          displayMaxLength
          validators={{ required: true }}
          css={{
            'abyss-text-input-area-label': {
              position: 'relative',
              fontSize: '15px',
            },
          }}
        />
        <TextInputArea
          id="clientActions"
          label="Client Actions"
          model="clientActions"
          displayMaxLength
          validators={{ required: true }}
          css={{
            'abyss-text-input-area-label': {
              position: 'relative',
              fontSize: '15px',
            },
          }}
        />
        <RadioGroup
          label="Do you need to attach any files?"
          model="isFilesAvailable"
          display="row"
          validators={{ required: true }}
          css={{
            'abyss-radio-group-label': {
              position: 'relative',
              fontSize: '15px',
            },
          }}
        >
          <RadioGroup.Radio label="Yes" value="yes" />
          <RadioGroup.Radio label="No" value="no" />
        </RadioGroup>
        {isFilesAvailable === 'yes' && (
          <FileUpload
            headerContent={headerContent}
            fileTypes={fileTypesAccepted}
            css={{
              'abyss-file-upload-body-container': {
                paddingTop: '20px',
                paddingBottom: '20px',
                fontSize: '15px',
              },
              'abyss-file-upload-label': {
                position: 'absolute',
                fontSize: '15px',
              },
              'abyss-file-upload-error-list-container': {
                fontSize: '15px',
              },
            }}
            model="fileUpload"
            onChange={setDraftFiles}
            maxFileSize={2.5}
            maxFiles={2}
            validators={{
              required:
                'You have chosen Yes for attachment, please attach a valid PDF file.',
            }}
            uploadMessage="Drag your .pdf file here"
            customValidation={validateFile}
            isUploading={downloadFile?.loading}
          />
        )}
      </Layout.Stack>
    </Wrapper>
  );
};

CreateCommunicationFormFields.propTypes = {
  setDraftFiles: PropTypes.func.isRequired,
};
